import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { formatIndianCurrency, formatIndianDate } from '../../utils/formatters';
import { TrendingUp, Plus, X } from 'lucide-react';

const AdminPerformance = () => {
  const [periods, setPeriods] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    planId: '',
    periodStart: '',
    periodEnd: '',
    performancePercent: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [periodsRes, plansRes] = await Promise.all([
        api.get('/admin/performance-periods'),
        api.get('/plans'),
      ]);
      setPeriods(periodsRes.data);
      setPlans(plansRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await api.post('/admin/performance-periods', {
        planId: formData.planId,
        periodStart: new Date(formData.periodStart).toISOString(),
        periodEnd: new Date(formData.periodEnd).toISOString(),
        performancePercent: parseFloat(formData.performancePercent),
      });
      alert('Performance period created successfully!');
      setShowCreateForm(false);
      setFormData({ planId: '', periodStart: '', periodEnd: '', performancePercent: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create period');
    }

    setCreating(false);
  };

  const handleClosePeriod = async (periodId) => {
    if (!window.confirm('This will post profits and commissions. Continue?')) {
      return;
    }

    try {
      const res = await api.post(`/admin/performance-periods/${periodId}/close`);
      alert(`Success! ${res.data.total_deposits} deposits processed, ₹${res.data.total_profits.toFixed(2)} total profits posted.`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to close period');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-gray-900">Performance Periods</h1>
            <p className="text-gray-600 mt-1">Manage profit posting and commissions</p>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold px-6 py-3 rounded-lg"
          >
            {showCreateForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            {showCreateForm ? 'Cancel' : 'Create Period'}
          </button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Create Performance Period</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Plan *</label>
                  <select
                    value={formData.planId}
                    onChange={(e) => setFormData({ ...formData, planId: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select Plan</option>
                    {plans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} (Max: {plan.maxMonthlyReturnPercent}%)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Performance % *</label>
                  <input
                    type="number"
                    step="0.1"
                    max={plans.find(p => p.id === formData.planId)?.maxMonthlyReturnPercent || 100}
                    value={formData.performancePercent}
                    onChange={(e) => setFormData({ ...formData, performancePercent: e.target.value })}
                    placeholder="e.g., 5.5"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Period Start *</label>
                  <input
                    type="date"
                    value={formData.periodStart}
                    onChange={(e) => setFormData({ ...formData, periodStart: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Period End *</label>
                  <input
                    type="date"
                    value={formData.periodEnd}
                    onChange={(e) => setFormData({ ...formData, periodEnd: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={creating}
                className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 rounded-lg disabled:bg-gray-400"
              >
                {creating ? 'Creating...' : 'Create Period'}
              </button>
            </form>
          </div>
        )}

        {/* Periods List */}
        <div className="space-y-4">
          {periods.map((period) => (
            <div key={period.id} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">{period.planName}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {formatIndianDate(period.periodStart)} - {formatIndianDate(period.periodEnd)}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  period.status === 'closed' ? 'bg-success-light text-success' :
                  period.status === 'active' ? 'bg-primary-light text-primary' :
                  'bg-gray-200 text-gray-600'
                }`}>
                  {period.status.toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Performance %</p>
                  <p className="text-xl font-bold text-primary">{period.performancePercent}%</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Status</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize">{period.status}</p>
                </div>
              </div>

              {period.status === 'draft' && (
                <button
                  onClick={() => handleClosePeriod(period.id)}
                  className="w-full bg-success hover:bg-success-dark text-white font-semibold py-3 rounded-lg"
                >
                  Close Period & Post Profits
                </button>
              )}
            </div>
          ))}
          
          {periods.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No performance periods yet</p>
              <p className="text-sm text-gray-500 mt-1">Create your first period to start posting profits</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminPerformance;